import { debounce, cloneDeep } from 'lodash-es';
import { useAudienceEstimateApi, useRequest, useStore, StateEnum } from '@tt4b/ttam-hooks';
import { computed, defineComponent, ref, toRefs } from '@vue/composition-api';
import {
  BEHAVIOR_DAYS,
  BEHAVIOR_TARGETING_SCENES_TYPE,
  InterestScene,
  VIDEO_ACTIONS,
  CREATOR_ACTIONS,
  InterestBehaviorTab,
  AudienceEstimate,
} from '@tt4b/ttam-enums';
import { i18n } from './../../../i18n';
import { generateAdgroupTargetFormData } from '@tt4b/ttam-utils';

import { HASHTAG_TARGETING_CONSTANT_FIELDS } from './../../../hooks/creation/adgroup/useBehavior/index';
import { props as compProps } from './consts';
import { usePropsEstimateCount } from './../../adgroup/audience-size/hooks';

type InterestOptionItem = {
  id: string;
  keyword_id: string;
  scene: InterestScene;
  description: string;
  parent?: InterestOptionItem;
};

const name = 'ttam-tree-node-hover-estiamte';

export default defineComponent({
  name,
  props: compProps,

  // eslint-disable-next-line max-lines-per-function
  setup(props) {
    const { type, label, adgroupData } = toRefs(props);
    const { run, isLoading: audienceLoading } = useRequest(useAudienceEstimateApi, {} as any, {
      manual: true,
    });
    const { state } = useStore();

    /** 数据节点 */
    const node = ref<InterestOptionItem>({} as InterestOptionItem);
    const audienceSize = ref<string>('');

    const isInterestKeyword = computed(() => props.type === 'interest' && node.value.scene === InterestScene.KEYWORD);
    const isHashtagBehavior = computed(() => props.type === 'hashtag');
    const showBreadcrumbs = computed(() => props.isFiveInOneNewVersion || !isHashtagBehavior.value);

    function getLabel(currentNode) {
      return (currentNode || {})[label.value];
    }

    const title = computed(() => getLabel(node.value));

    const breadcrumbs = computed(() => {
      let temp = node.value;
      if (isInterestKeyword.value) {
        if (props.isFiveInOneNewVersion) {
          return `${i18n.t('adgroup_interest_keywords_additional_interests')} > ${getLabel(temp)}`;
        }
        return i18n.t('adgroup_interest_keywords_additional_interests');
      } else {
        let str = '';
        const breadcrumbsTitle = InterestBehaviorTab()[temp.scene]?.title || '';
        while (getLabel(temp)) {
          str = `${getLabel(temp)} ${str.length ? '>' : ''} ${str}`;
          temp = temp?.parent ?? ({} as InterestOptionItem);
        }
        if (props.isFiveInOneNewVersion && breadcrumbsTitle) {
          return `${breadcrumbsTitle} > ${str}`;
        }
        return str;
      }
    });

    const description = computed(() => {
      if (node.value.description) {
        return node.value.description;
      }

      return i18n.t('module_adgroup_interest_option_description', {
        interest: node.value[label.value],
      });
    });

    /** 由外部主动调用，展示预估人数 */
    async function _showHoverEstimate(nodeItem: InterestOptionItem) {
      node.value = nodeItem;
      const { particleLocations, zipcodeIds } = adgroupData.value;
      const areaTargetData = { particleLocations, zipcodeIds };
      const baseTargetFormData = generateAdgroupTargetFormData();

      let param: Record<string, any> = {
        ...adgroupData.value,
        ...baseTargetFormData,
        ...areaTargetData,
      };
      if (type.value === 'inMarket') {
        param = {
          ...param,
          inMarketTags: [node.value.id],
        };
      } else if (type.value === 'interest') {
        if (node.value.scene === InterestScene.KEYWORD) {
          param = {
            ...param,
            interestKeywordsI18n: [node.value.keyword_id || node.value.id],
          };
        } else {
          param = {
            ...param,
            adTagV2: [node.value.id],
          };
        }
      } else {
        const index = adgroupData.value.actionScenesV2.indexOf(BEHAVIOR_TARGETING_SCENES_TYPE[type.value]);
        let actions = adgroupData.value.videoActionsV2[index];
        let day = adgroupData.value.actionDaysV2[index];

        if (type.value === 'video') {
          if (!actions?.length) {
            actions = VIDEO_ACTIONS().map(item => item.value);
          }
          if (!day) {
            // 五合一默认15天
            day = props.isFiveInOneNewVersion ? BEHAVIOR_DAYS.IN_15_DAY.id : BEHAVIOR_DAYS.IN_7_DAY.id;
          }
        } else if (type.value === 'creator') {
          if (!actions?.length) {
            actions = CREATOR_ACTIONS().map(item => item.value);
          }
        } else if (type.value === 'hashtag') {
          ({ day, actions } = cloneDeep(HASHTAG_TARGETING_CONSTANT_FIELDS));
        }

        const data = {
          actionScenesV2: [BEHAVIOR_TARGETING_SCENES_TYPE[type.value]],
          actionCategoriesV2: [[node.value.id]],
          actionDaysV2: [day || 0],
          videoActionsV2: [actions || []],
        };
        param = { ...param, ...data };
      }

      if (!param.particleLocations?.length && !param.zipcodeIds?.length) {
        audienceSize.value = '-';

        return;
      }

      const data = await run({
        data: param,
        isSplitTest: false,
        withSignature: false,
        locationParams: {
          areaMap: state[StateEnum.areaData].data.value?.areaMap ?? {},
          isSplitTestBidding: false,
          isSplitTestCreative: false,
        },
      });
      audienceSize.value = data?.userCount
      ? usePropsEstimateCount({ estimateResult: ref(data as unknown as AudienceEstimate) }).estimateCount.value
      : '-';
    }

    const showHoverEstimateDebounce = debounce(_showHoverEstimate, 350);

    /** 对于子级兴趣类目词返回其顶级祖先的 label，对于顶级兴趣类目词返回"Interest"的 i18n */
    function findAncestorLabel(currentNode, depth = 0) {
      if (currentNode.parent?.i18n_value) {
        return findAncestorLabel(currentNode.parent, depth + 1);
      }

      return depth ? currentNode.i18n_value : i18n.t('module_adgroup_behavior_interest_label');
    }

    const interestClass = computed(() => {
      if (!props.showInterestClass || props.type !== 'interest') {
        return '';
      }

      if (node.value.scene === InterestScene.CATEGORY) {
        return findAncestorLabel(node.value);
      } else {
        return i18n.t('adgroup_interest_keywords_additional_interests');
      }
    });

    return {
      name,
      showBreadcrumbs,
      title,
      description,
      breadcrumbs,
      audienceSize,
      audienceLoading,
      showHoverEstimate: showHoverEstimateDebounce,
      isInterestKeyword,
      interestClass,
    };
  },
});
